import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  TextField,
  Button,
  Divider,
  FormControlLabel,
  Checkbox,
  Card,
  CardContent,
  Radio,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import phonepeIcon from "../img/phonepay.png";
import upiIcon from "../img/upi.jpg";
import { useTheme } from "@mui/material";
import { useAuth } from "../context/AuthProvider";
const CheckoutPage = () => {
  const { planId } = useParams();
  const { authToken, userId } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();

  const [form, setForm] = useState({
    name: "",
    email: "",
    address: "",
    city: "",
    zip: "",
    saveInfo: false,
  });
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showPayment, setShowPayment] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const token = localStorage.getItem("access_token");
  const INR_CONVERSION_RATE = 86.46;

  useEffect(() => {
    const fetchPlanDetails = async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_NEW_HOST_URL}/athrv/api/v1/plan?plan_id=${planId}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              Accept: "application/json",
            },
          }
        );
        const data = await res.json();
        setSelectedPlan(data);
      } catch (err) {
        console.error(err);
        setError("Failed to load plan.");
      } finally {
        setLoading(false);
      }
    };
    fetchPlanDetails();
  }, [planId]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handlePaymentSelect = (method) => {
    setPaymentMethod(method);
  };

  const handleFreePlanSubmit = async () => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_NEW_HOST_URL}/athrv/api/v1/organizations/set-plan`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            user_id: userId,
            plan_id: planId,
          }),
        }
      );

      if (!res.ok) throw new Error("Failed to store plan");

      navigate("/dashboard");
    } catch (error) {
      console.error("Error storing plan:", error);
      alert("Error submitting free plan.");
    }
  };

  if (loading)
    return (
      <Box sx={{ p: 6, textAlign: "center" }}>
        <Typography variant="h6">Loading plan...</Typography>
      </Box>
    );

  if (error || !selectedPlan)
    return (
      <Box sx={{ p: 6, textAlign: "center" }}>
        <Typography variant="h6" color="error">
          {error}
        </Typography>
      </Box>
    );

  const isFreePlan = selectedPlan.plan_name?.toLowerCase() === "free";

  const priceInINR = selectedPlan.price_per_seat * INR_CONVERSION_RATE;
  const subtotal = priceInINR;
  const tax = subtotal * 0.1;
  const total = subtotal + tax;

  const paymentOptions = [
    {
      name: "Cash on Delivery",
      value: "cash",
      icon: "https://img.icons8.com/ios/50/cash-in-hand.png",
    },
    {
      name: "Razorpay",
      value: "razorpay",
      icon: "https://cdn.razorpay.com/logo.svg",
    },
    {
      name: "Google Pay",
      value: "gpay",
      icon: "https://img.icons8.com/color/48/google-pay-india.png",
    },
    {
      name: "PhonePe",
      value: "phonepe",
      icon: phonepeIcon,
    },
    {
      name: "UPI",
      value: "upi",
      icon: upiIcon,
    },
  ];

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: theme.palette.mode === "dark" ? "#121212" : "#f5f5f5",
        padding: 4,
      }}
    >
      <Box
        sx={{
          px: { xs: 2, md: 6 },
          py: 4,
          backgroundColor:
            theme.palette.mode === "dark" ? "#282828" : "#f5f5f5",
          minHeight: "100vh",
        }}
      >
        <Paper elevation={6} sx={{ p: 3, borderRadius: 4 }}>
          <Typography
            variant="h4"
            fontWeight="bold"
            sx={{
              textAlign: "center",
              color: theme.palette.mode === "dark" ? "#ffffff" : "#1a237e",
              mb: 4,
            }}
          >
            Checkout
          </Typography>

          <Grid container spacing={4}>
            {/* Billing Form */}
            <Grid item xs={12} md={7}>
              <Paper
                elevation={4}
                sx={{
                  p: 4,
                  mb: 4,
                  borderRadius: 3,
                  backgroundColor:
                    theme.palette.mode === "dark" ? "#121212" : "#fff",
                }}
              >
                <Typography variant="h6" fontWeight="600" gutterBottom>
                  Billing Information
                </Typography>
                <TextField
                  fullWidth
                  label="Full Name"
                  name="name"
                  margin="normal"
                  value={form.name}
                  onChange={handleChange}
                />
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  margin="normal"
                  value={form.email}
                  onChange={handleChange}
                />
                <TextField
                  fullWidth
                  label="Shipping Address"
                  name="address"
                  margin="normal"
                  value={form.address}
                  onChange={handleChange}
                />
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="City"
                      name="city"
                      margin="normal"
                      value={form.city}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="ZIP Code"
                      name="zip"
                      margin="normal"
                      value={form.zip}
                      onChange={handleChange}
                    />
                  </Grid>
                </Grid>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="saveInfo"
                      checked={form.saveInfo}
                      onChange={handleChange}
                    />
                  }
                  label="Save this info"
                />
              </Paper>
            </Grid>

            {/* Order Summary & Payment */}
            <Grid item xs={12} md={5}>
              <Card elevation={4} sx={{ borderRadius: 3 }}>
                <CardContent sx={{ p: 4 }}>
                  <Typography
                    variant="h6"
                    fontWeight="bold"
                    sx={{ color: "#1565c0", mb: 2 }}
                  >
                    Order Summary
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      color: "#555",
                      mb: 1,
                    }}
                  >
                    <Typography>{selectedPlan.plan_name}</Typography>
                    <Typography>
                      {isFreePlan ? "Free" : `₹${priceInINR.toFixed(2)}`}
                    </Typography>
                  </Box>
                  {!isFreePlan && (
                    <>
                      <Divider sx={{ my: 2 }} />
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography>Subtotal</Typography>
                        <Typography>₹{subtotal.toFixed(2)}</Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mt: 1,
                        }}
                      >
                        <Typography>Tax (10%)</Typography>
                        <Typography>₹{tax.toFixed(2)}</Typography>
                      </Box>
                      <Divider sx={{ my: 2 }} />
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography variant="h6">Total</Typography>
                        <Typography variant="h6">
                          ₹{total.toFixed(2)}
                        </Typography>
                      </Box>
                    </>
                  )}

                  {isFreePlan ? (
                    <Button
                      variant="contained"
                      fullWidth
                      size="large"
                      color="success"
                      sx={{ mt: 3, fontWeight: "bold", borderRadius: 2 }}
                      onClick={handleFreePlanSubmit}
                    >
                      Submit
                    </Button>
                  ) : (
                    <>
                      <Button
                        variant="contained"
                        fullWidth
                        size="large"
                        sx={{
                          mt: 3,
                          background:
                            "linear-gradient(45deg, #1e88e5, #42a5f5)",
                          fontWeight: "bold",
                          borderRadius: 2,
                        }}
                        onClick={() => setShowPayment(true)}
                      >
                        Continue to Payment
                      </Button>

                      {showPayment && (
                        <Box sx={{ mt: 4 }}>
                          <Typography
                            variant="subtitle1"
                            fontWeight="bold"
                            gutterBottom
                          >
                            Choose Payment Method
                          </Typography>
                          <Grid container spacing={2}>
                            {paymentOptions.map((opt) => (
                              <Grid item xs={12} key={opt.value}>
                                <Paper
                                  elevation={
                                    paymentMethod === opt.value ? 6 : 2
                                  }
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    p: 2,
                                    borderRadius: 2,
                                    border:
                                      paymentMethod === opt.value
                                        ? "2px solid #673ab7"
                                        : "1px solid #ccc",
                                    cursor: "pointer",
                                  }}
                                  onClick={() => handlePaymentSelect(opt.value)}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                    }}
                                  >
                                    <img
                                      src={opt.icon}
                                      alt={opt.name}
                                      style={{ width: 40, marginRight: 12 }}
                                    />
                                    <Typography variant="body1">
                                      {opt.name}
                                    </Typography>
                                  </Box>
                                  <Radio
                                    checked={paymentMethod === opt.value}
                                    value={opt.value}
                                    color="secondary"
                                  />
                                </Paper>
                              </Grid>
                            ))}
                          </Grid>
                          <Button
                            variant="contained"
                            fullWidth
                            size="large"
                            color="success"
                            sx={{ mt: 3, fontWeight: "bold", borderRadius: 2 }}
                            onClick={() =>
                              alert(
                                `Payment method: ${paymentMethod} | ₹${total.toFixed(
                                  2
                                )}`
                              )
                            }
                          >
                            Pay ₹{total.toFixed(2)}
                          </Button>
                        </Box>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Paper>
      </Box>
    </Box>
  );
};

export default CheckoutPage;
